import { useEffect, useRef } from 'react';

interface UseAutofocusOnVisibleOptions {
  shouldAutoFocus?: boolean;
  selectText?: boolean;
}

/**
 * Custom hook that automatically focuses (and optionally selects) an input element
 * when it becomes visible in the viewport. This is useful for handling autofocus
 * when the input is inside a modal or other element that is not immediately visible.
 *
 * @param options Configuration options
 * @param options.shouldAutoFocus Whether to enable autofocus behavior
 * @param options.selectText Whether to select all text after focusing
 * @returns A ref to attach to the input element
 */
export function useAutofocusOnVisible<
  T extends HTMLInputElement | HTMLTextAreaElement,
>({
  shouldAutoFocus = false,
  selectText = false,
}: UseAutofocusOnVisibleOptions = {}) {
  const elementRef = useRef<T>(null);

  useEffect(() => {
    if (!shouldAutoFocus || !elementRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && elementRef.current) {
            elementRef.current.focus();

            if (selectText) {
              elementRef.current.select();
            }

            // Stop observing after first successful focus
            observer.disconnect();
          }
        });
      },
      {
        threshold: 0,
        root: null,
      }
    );

    observer.observe(elementRef.current);

    return () => observer.disconnect();
  }, [shouldAutoFocus, selectText]);

  return elementRef;
}
